// import './wdyr'

import { LicenseInfo } from '@mui/x-license-pro'
import * as Sentry from "@sentry/react";
import 'filepond/dist/filepond.min.css'
import 'react-confirm-alert/src/react-confirm-alert.css'
import ReactDOM from 'react-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import 'url-search-params-polyfill'
import api from './api'

import App from './App'
import LoadSpinner from './components/loadSpinner/LoadSpinner'
import './i18n'
import './index.sass'
import { persistor, store } from './store'
import { isCordova } from './util'
import { initWebPush } from './push/webPush'
import { reloadForNewVersion } from './components/AsyncErrorBoundary'

// Vite fires this when a page's code from the previous deploy can't be loaded any more:
// reload once to get the current version instead of hanging on a spinner
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForNewVersion()) event.preventDefault()
})

LicenseInfo.setLicenseKey(
  '6a515dfcef5f5bae4b86de8435c76cf6T1JERVI6NDMwNjgsRVhQSVJZPTE2ODMyNzA3OTAwMDAsS0VZVkVSU0lPTj0x'
)

function configureOneSignal() {
  if (isCordova()) {
    console.debug('setting up one signal')
    console.debug('one signal app id', process.env.REACT_APP_ONE_SIGNAL_APP_ID)

    const oneSignal = (window as any).plugins.OneSignal
    if (!oneSignal) {
      console.error('one signal is not defined')
      return
    }

    oneSignal.setLogLevel(6, 0); // VERBOSE, NONE
    
    const notificationOpenedCallback = (jsonData) => {
      console.log(`notificationOpenedCallback: ${JSON.stringify(jsonData)}`)
    }

    const iosSettings: any = {}
    iosSettings.kOSSettingsKeyAutoPrompt = true
    iosSettings.kOSSettingsKeyInAppLaunchURL = false

    if (typeof oneSignal.setAppId === 'function') {
      oneSignal.setAppId(process.env.REACT_APP_ONE_SIGNAL_APP_ID)
      oneSignal.setNotificationOpenedHandler(notificationOpenedCallback)
    } else {
      oneSignal
        .startInit(process.env.REACT_APP_ONE_SIGNAL_APP_ID)
        .handleNotificationOpened(notificationOpenedCallback)
        .iOSSettings(iosSettings)
        .inFocusDisplaying(oneSignal.OSInFocusDisplayOption.Notification)
        .endInit()
    }

    // The promptForPushNotificationsWithUserResponse
    // function will show the iOS push notification prompt.
    // We recommend removing the following code and
    // instead using an In-App Message to prompt for notification permission (See step 6)
    oneSignal.promptForPushNotificationsWithUserResponse((accepted) => {
      console.log(`User accepted notifications: ${accepted}`)
    })
  }
}

// the app shell worker, also OneSignal's push worker (src/serviceWorker/OneSignalSDKWorker.ts): the app starts without network
function registerAppWorker() {
  if (process.env.NODE_ENV !== 'production' || isCordova() || !('serviceWorker' in navigator)) return
  navigator.serviceWorker.register('/OneSignalSDKWorker.js', { scope: '/' }).catch((error) => {
    console.warn('service worker not registered', error)
  })
}

const startApp = async () => {
  if (process.env.NODE_ENV === 'production') {
    configureOneSignal()
  }
  registerAppWorker()
  initWebPush()

  if (isCordova()) {
    window.open = (window as any).cordova.InAppBrowser.open
  }
  const jwt = localStorage.getItem('jwt')
  if (jwt) {
    api.addToken(jwt)
  }

  ReactDOM.render(
    (
      <HelmetProvider>
        <Provider store={store}>
          <PersistGate loading={<LoadSpinner className="centered" />} persistor={persistor}>
            <App />
          </PersistGate>
        </Provider>
      </HelmetProvider>
    ), document.getElementById('root')
  )
}

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    release: `alphaprocess@${process.env.REACT_APP_COMMIT_SHA}`,
    ignoreErrors: [
      'Non-Error promise rejection captured',
      'Network Error',
      'Request aborted',
      // an expired or missing login: the app sends the user to the login page, not an error
      'Request failed with status code 401'
    ]
  })
}
const { cordova, StatusBar } = (window as any)
if (cordova) {
  if (StatusBar) {
    StatusBar.styleDefault()
  }
  document.addEventListener('deviceready', startApp, false)
} else {
  startApp()
}
